import { useState, useEffect } from 'react';
import { Box, Typography, useTheme, useMediaQuery, Chip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { tokens } from '../../theme';
import { formatArrivalTime, getUtcOffsetMinutes } from '../../utils/units';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import NotListedLocationIcon from '@mui/icons-material/NotListedLocation';
import FlagIcon from '@mui/icons-material/Flag';
import GroupIcon from '@mui/icons-material/Group';
import RamenDiningIcon from '@mui/icons-material/RamenDining';
import LuggageIcon from '@mui/icons-material/Luggage';
import HotelIcon from '@mui/icons-material/Hotel';
import TimerIcon from '@mui/icons-material/Timer';

const formatStopTime = (seconds) => {
  if (!seconds || seconds < 5) return '-';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const formatElapsed = (seconds) => {
  if (seconds == null) return '-';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${m}m`;
};

// Parse "Day X HH:MM" cutoff string to absolute milliseconds (UTC epoch)
// Cutoff times are expressed in the recording timezone (same as workoutStartTime offset)
const parseCutoffMs = (cutoffStr, workoutStartTime) => {
  if (!cutoffStr || !workoutStartTime) return null;
  const match = cutoffStr.match(/Day\s*(\d+)\s+(\d{1,2}):(\d{2})/i);
  if (!match) return null;
  const dayNum = parseInt(match[1], 10);
  const hours = parseInt(match[2], 10);
  const minutes = parseInt(match[3], 10);
  const startMs = new Date(workoutStartTime).getTime();
  const offsetMinutes = getUtcOffsetMinutes(workoutStartTime);
  const offsetMs = (offsetMinutes !== null ? offsetMinutes : 0) * 60000;
  const startLocalMs = startMs + offsetMs;
  const startLocalDate = new Date(startLocalMs);
  const startMidnightLocalMs = startLocalMs - (
    startLocalDate.getUTCHours() * 3600000 +
    startLocalDate.getUTCMinutes() * 60000 +
    startLocalDate.getUTCSeconds() * 1000 +
    startLocalDate.getUTCMilliseconds()
  );
  // Cutoff expressed in local time → convert back to UTC epoch ms
  const cutoffLocalMs = startMidnightLocalMs + (dayNum - 1) * 86400000 + hours * 3600000 + minutes * 60000;
  return cutoffLocalMs - offsetMs;
};

const UNPLANNED_COLOR = '#ff9800';
const UNDETECTED_COLOR = '#9e9e9e';
const STOP_TIME_COLOR = '#64b5f6'; // light blue — distinct from unplanned orange

const getUnits = () => localStorage.getItem('units') || 'metric';
const kmToMi = (km) => km != null ? km * 0.621371 : null;
const fmtDist = (km, units) => {
  if (km == null) return '-';
  if (units === 'imperial') return `${kmToMi(km).toFixed(2)} mi`;
  return `${km} km`;
};

const RunningCheckpointsGrid = ({ race, checkpoints = [], workoutStartTime = null, title = 'Race Checkpoints', onCheckpointDoubleClick = null }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [zoomedRowId, setZoomedRowId] = useState(null);
  const [units, setUnits] = useState(getUnits());

  useEffect(() => {
    const handler = (e) => setUnits(e.detail || getUnits());
    window.addEventListener('unitsChanged', handler);
    return () => window.removeEventListener('unitsChanged', handler);
  }, []);

  // Use enhanced analyzed checkpoints if provided, fall back to race checkpoints
  const displayCheckpoints = checkpoints.length > 0 ? checkpoints : (race?.checkpoints || []);

  if (displayCheckpoints.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', backgroundColor: colors.grey[800], borderRadius: '8px' }}>
        <Typography color={colors.grey[300]}>
          No checkpoint information available
        </Typography>
      </Box>
    );
  }

  const hasAnalyzedData = checkpoints.length > 0 && checkpoints[0].fit_distance_km !== undefined;
  const hasLiveData = checkpoints.some((cp) => cp.live_elapsed_s !== undefined);
  // Index (within `checkpoints`, matches row.id) of the last checkpoint we have a live split for —
  // rows before it with no split just mean the timing mat data wasn't matched/reported, not "not yet".
  const lastLiveIndex = hasLiveData
    ? checkpoints.reduce((acc, cp, idx) => (cp.live_elapsed_s != null ? idx : acc), -1)
    : -1;

  const columns = [
    {
      field: 'name',
      headerName: 'Checkpoint',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const row = params.row;
        const isUnplanned = row.type === 'unplanned_stop';
        const isUndetected = row.status === 'undetected';
        const nameColor = isUnplanned ? UNPLANNED_COLOR : isUndetected ? UNDETECTED_COLOR : colors.grey[100];
        return (
          <Box display="flex" alignItems="center" gap={0.5}>
            {isUnplanned && <FlagIcon sx={{ fontSize: 14, color: UNPLANNED_COLOR }} />}
            <Typography variant="body2" fontWeight="bold" color={nameColor}>
              {params.value}
            </Typography>
          </Box>
        );
      }
    },
    ...(race ? [{
      field: 'services',
      headerName: '',
      width: 130,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;
        const services = params.row.services?.toLowerCase() || '';
        const isFood = services.includes('food');
        const isDrinks = services.includes('drinks');
        const hasCrew = row.assistance_permitted;
        const hasWarmMeal = row.warm_meal === true;
        const hasSpareBag = row.spare_bag === true;
        const hasRestArea = row.rest_area === true;
        const hasAny = isFood || isDrinks || hasCrew || hasWarmMeal || hasSpareBag || hasRestArea;
        return (
          <Box display="flex" gap={0.5} alignItems="center">
            {isFood && <LocalDiningIcon sx={{ fontSize: 18, color: colors.greenAccent[500] }} title="Food" />}
            {isDrinks && <LocalCafeIcon sx={{ fontSize: 18, color: colors.blueAccent[500] }} title="Drinks" />}
            {hasWarmMeal && <RamenDiningIcon sx={{ fontSize: 18, color: '#ff9800' }} title="Warm meal" />}
            {hasSpareBag && <LuggageIcon sx={{ fontSize: 18, color: colors.blueAccent[300] }} title="Spare bag" />}
            {hasRestArea && <HotelIcon sx={{ fontSize: 18, color: colors.greenAccent[300] }} title="Rest area" />}
            {hasCrew && <GroupIcon sx={{ fontSize: 18, color: colors.grey[300] }} title="Crew access" />}
            {!hasAny && (
              <TimerIcon sx={{ fontSize: 18, color: colors.grey[500] }} title="Timing point only" />
            )}
          </Box>
        );
      }
    }, {
      field: 'distance_km',
      headerName: units === 'imperial' ? 'Official mi' : 'Official km',
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2" color={colors.grey[300]}>
          {params.row.type === 'unplanned_stop' ? '-' : fmtDist(params.value, units)}
        </Typography>
      )
    }] : []),
    ...(hasAnalyzedData ? [
      ...(hasLiveData ? [
        {
          field: 'live_elapsed_s',
          headerName: 'Live',
          width: 100,
          renderCell: (params) => {
            const formatted = params.value != null ? formatElapsed(params.value) : null;
            if (formatted) {
              return (
                <Typography variant="body2" color={colors.redAccent?.[400] || '#ef5350'} fontWeight="bold">
                  {formatted}
                </Typography>
              );
            }
            // Before the last checkpoint we actually have a split for, a blank means the
            // timing mat wasn't matched/reported — not that the runner hasn't gotten there.
            const isSkippedPast = params.row.id < lastLiveIndex;
            return (
              <Typography variant="caption" color={colors.grey[500]}>
                {isSkippedPast ? '-' : 'not yet'}
              </Typography>
            );
          }
        },
        {
          field: 'delta_s',
          headerName: 'vs. Plan',
          width: 100,
          renderCell: (params) => {
            const val = params.value;
            if (val == null) return <Typography variant="body2" color={colors.grey[500]}>-</Typography>;
            const isBehind = val > 0;
            const color = isBehind ? (colors.redAccent?.[400] || '#ef5350') : (colors.greenAccent?.[400] || '#66bb6a');
            const formatted = formatElapsed(Math.abs(val));
            return (
              <Typography variant="body2" fontWeight="bold" color={color}>
                {isBehind ? '+' : '-'}{formatted}
              </Typography>
            );
          }
        },
        {
          field: 'estimated_elapsed_s',
          headerName: 'Est. Elapsed',
          width: 110,
          renderCell: (params) => {
            const formatted = params.value != null ? formatElapsed(params.value) : null;
            return formatted ? (
              <Typography variant="body2" fontStyle="italic" color={colors.blueAccent?.[300] || '#64b5f6'}>
                {formatted}
              </Typography>
            ) : (
              <Typography variant="body2" color={colors.grey[500]}>-</Typography>
            );
          }
        },
        {
          field: 'estimated_arrival_time',
          headerName: 'Est. Timing',
          width: 120,
          valueGetter: (value, row) => row.estimated_elapsed_s,
          renderCell: (params) => {
            const formatted = params.value != null ? formatArrivalTime(params.value, workoutStartTime) : null;
            return formatted && formatted !== '-' ? (
              <Typography variant="caption" fontStyle="italic" color={colors.blueAccent?.[300] || '#64b5f6'}>
                {formatted}
              </Typography>
            ) : (
              <Typography variant="caption" color={colors.grey[500]}>-</Typography>
            );
          }
        },
      ] : []),
      {
        field: 'stopping_time_seconds',
        headerName: 'Stop Time',
        width: 110,
        renderCell: (params) => {
          const formatted = formatStopTime(params.value);
          return (
            <Box display="flex" alignItems="center" gap={0.5}>
              {formatted !== '-' && (
                <PauseCircleIcon sx={{ fontSize: 14, color: STOP_TIME_COLOR }} />
              )}
              <Typography variant="body2" color={formatted !== '-' ? STOP_TIME_COLOR : colors.grey[500]}>
                {formatted}
              </Typography>
            </Box>
          );
        }
      },
      {
        field: 'arrival_elapsed_seconds',
        headerName: 'Timing',
        width: 120,
        renderCell: (params) => {
          const formatted = formatArrivalTime(params.value, workoutStartTime);
          return (
            <Typography variant="caption" color={formatted !== '-' ? colors.grey[100] : colors.grey[500]}>
              {formatted}
            </Typography>
          );
        }
      },
      {
        field: 'arrival_elapsed_seconds_duration',
        headerName: 'Elapsed',
        width: 100,
        valueGetter: (value, row) => row.arrival_elapsed_seconds,
        renderCell: (params) => {
          const formatted = formatElapsed(params.value);
          return (
            <Typography variant="body2" color={formatted !== '-' ? colors.greenAccent[400] : colors.grey[500]}>
              {formatted}
            </Typography>
          );
        }
      },
    ] : []),
    ...(race ? [
      {
        field: 'cutoff_time',
        headerName: 'Cutoff',
        width: 120,
        renderCell: (params) => {
          const val = params.value;
          const isTime = val && /Day\s*\d+/i.test(val);
          return (
            <Typography variant="caption" color={isTime ? colors.redAccent[400] : colors.grey[500]}>
              {val || '-'}
            </Typography>
          );
        }
      },
      ...(hasAnalyzedData ? [
        {
          field: 'cutoff_time_diff',
          headerName: 'Time Diff',
          width: 110,
          valueGetter: (value, row) => {
            if (!row.cutoff_time || !workoutStartTime || row.arrival_elapsed_seconds == null) return null;
            const cutoffMs = parseCutoffMs(row.cutoff_time, workoutStartTime);
            if (cutoffMs == null) return null;
            const startMs = new Date(workoutStartTime).getTime();
            const arrivalMs = startMs + row.arrival_elapsed_seconds * 1000;
            return Math.round((arrivalMs - cutoffMs) / 60000); // positive = over cutoff, negative = under cutoff
          },
          renderCell: (params) => {
            const diff = params.value; // minutes; positive = bad (arrived late), negative = good (arrived early)
            if (diff == null) return <Typography variant="caption" color={colors.grey[500]}>-</Typography>;
            const isOver = diff >= 0;
            const color = isOver ? colors.redAccent[400] : diff >= -30 ? UNPLANNED_COLOR : colors.greenAccent[400];
            const abs = Math.abs(diff);
            const hh = String(Math.floor(abs / 60)).padStart(2, '0');
            const mm = String(abs % 60).padStart(2, '0');
            const label = `${isOver ? '+' : '-'}${hh}:${mm}`;
            return (
              <Typography variant="caption" fontWeight="bold" color={color}>
                {label}
              </Typography>
            );
          }
        },
      ] : []),
      {
        field: 'fastest_time',
        headerName: 'Fastest',
        width: 120,
        renderCell: (params) => (
          <Typography variant="caption" color={params.value ? colors.greenAccent[400] : colors.grey[500]}>
            {params.value || '-'}
          </Typography>
        )
      },
      {
        field: 'slowest_time',
        headerName: 'Slowest',
        width: 120,
        renderCell: (params) => (
          <Typography variant="caption" color={params.value ? colors.warnings?.orange || '#ff9800' : colors.grey[500]}>
            {params.value || '-'}
          </Typography>
        )
      },
    ] : []),
    ...(hasAnalyzedData ? [
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        renderCell: (params) => {
          const row = params.row;
          if (row.type === 'unplanned_stop') {
            return (
              <Chip label="Unplanned stop" size="small"
                sx={{ backgroundColor: '#7c4600', color: UNPLANNED_COLOR, fontSize: '0.7rem' }} />
            );
          }
          if (params.value === 'undetected') {
            return (
              <Chip
                icon={<NotListedLocationIcon sx={{ fontSize: 14, color: `${UNDETECTED_COLOR} !important` }} />}
                label="Not on course"
                size="small"
                sx={{ backgroundColor: colors.grey[700], color: UNDETECTED_COLOR, fontSize: '0.7rem' }}
              />
            );
          }
          return null;
        }
      }
    ] : []),
    // Actual/analyzed distance — kept at the end regardless of screen size, since the official
    // distance (when present) already anchors the row and this is more of a cross-check value.
    ...(hasAnalyzedData ? [
      {
        field: 'fit_distance_km',
        headerName: race ? (units === 'imperial' ? 'Actual mi' : 'Actual km') : 'Distance',
        width: 100,
        renderCell: (params) => (
          <Typography variant="body2" color={colors.grey[100]} fontWeight="bold">
            {fmtDist(params.value, units)}
          </Typography>
        )
      },
    ] : []),
  ];

  const rows = displayCheckpoints.map((checkpoint, index) => ({
    id: index,
    ...checkpoint
  }));

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" color={colors.grey[100]}>
            {title}
          </Typography>
          <Typography variant="body2" color={colors.grey[300]}>
            {race?.name ? `${race.name} - ` : ''}{rows.length} {race ? 'checkpoints' : 'stops'}
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          {race && (
            <>
              <Chip icon={<LocalDiningIcon />} label="Food" size="small"
                sx={{ backgroundColor: colors.greenAccent[700] }} />
              <Chip icon={<LocalCafeIcon />} label="Drinks" size="small"
                sx={{ backgroundColor: colors.blueAccent[700] }} />
              <Chip icon={<RamenDiningIcon sx={{ color: '#ff9800 !important' }} />} label="Warm meal" size="small"
                sx={{ backgroundColor: colors.grey[700] }} />
              <Chip icon={<LuggageIcon />} label="Spare bag" size="small"
                sx={{ backgroundColor: colors.grey[700] }} />
              <Chip icon={<HotelIcon />} label="Rest area" size="small"
                sx={{ backgroundColor: colors.grey[700] }} />
              <Chip icon={<GroupIcon />} label="Crew Access" size="small"
                sx={{ backgroundColor: colors.grey[700] }} />
              <Chip icon={<TimerIcon />} label="Timing only" size="small"
                sx={{ backgroundColor: colors.grey[700] }} />
            </>
          )}
          {hasAnalyzedData && (
            <>
              <Chip icon={<PauseCircleIcon sx={{ color: `${STOP_TIME_COLOR} !important` }} />} label="Stop Time" size="small"
                sx={{ backgroundColor: colors.grey[700], color: STOP_TIME_COLOR }} />
              <Chip icon={<FlagIcon sx={{ color: `${UNPLANNED_COLOR} !important` }} />}
                label="Unplanned stop" size="small"
                sx={{ backgroundColor: '#7c4600', color: UNPLANNED_COLOR }} />
              {race && (
                <Chip icon={<NotListedLocationIcon sx={{ color: `${UNDETECTED_COLOR} !important` }} />}
                  label="Not on course" size="small"
                  sx={{ backgroundColor: colors.grey[700], color: UNDETECTED_COLOR }} />
              )}
            </>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          height: Math.min(108 + rows.length * 52, 800),
          width: '100%',
          '& .MuiDataGrid-root': {
            border: 'none',
            backgroundColor: colors.grey[800]
          },
          '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${colors.grey[700]}`,
            color: colors.grey[100],
            display: 'flex',
            alignItems: 'center',
            fontSize: isMobile ? '0.7rem' : undefined,
          },
          '& .MuiDataGrid-cell .MuiTypography-root': {
            fontSize: isMobile ? '0.7rem' : undefined,
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: colors.grey[900],
            borderBottom: `2px solid ${colors.grey[700]}`,
            color: colors.grey[100]
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
            color: colors.grey[100],
            fontSize: isMobile ? '0.7rem' : undefined,
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: `1px solid ${colors.grey[700]}`,
            backgroundColor: colors.grey[900]
          },
          '& .MuiTablePagination-root': {
            color: colors.grey[100]
          },
          '& .row-undetected': {
            opacity: 0.5
          },
          '& .row-unplanned': {
            borderLeft: `3px solid ${UNPLANNED_COLOR}`
          },
          '& .MuiDataGrid-row': {
            '&:hover': { backgroundColor: colors.grey[700] }
          },
          '& .row-zoomed': {
            backgroundColor: colors.grey[600],
            '&:hover': { backgroundColor: colors.grey[500] }
          }
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={50}
          rowsPerPageOptions={[25, 50, 100]}
          disableSelectionOnClick
          onRowDoubleClick={hasAnalyzedData && onCheckpointDoubleClick ? (params) => {
            const idx = params.row.id;
            const current = displayCheckpoints[idx];
            if (current?.fit_distance_km == null) return;
            const previous = idx > 0 ? displayCheckpoints[idx - 1] : null;
            const left = previous?.fit_distance_km ?? 0;
            const right = current.fit_distance_km;
            setZoomedRowId(idx);
            onCheckpointDoubleClick({ leftKm: left, rightKm: right });
          } : undefined}
          getRowClassName={(params) => {
            if (params.row.id === zoomedRowId) return 'row-zoomed';
            if (params.row.status === 'undetected') return 'row-undetected';
            if (params.row.type === 'unplanned_stop') return 'row-unplanned';
            return '';
          }}
        />
      </Box>
    </Box>
  );
};

export default RunningCheckpointsGrid;
